(() => {
  const RANGE_LABELS = { length: 'length', volume: 'volume' };

  function parseValue(text, key) {
    text = String(text || '').trim().toLowerCase();
    if (key === 'volume') {
      const m = text.replace(',', '.').match(/-?\d+(?:\.\d+)?/);
      return m ? Number(m[0]) : NaN;
    }
    const m = text.match(/(\d+)\s*['’]\s*(\d{1,2})/);
    if (!m) return NaN;
    return Number(m[1]) * 12 + Number(m[2]);
  }

  function formatValue(value, key) {
    if (key === 'volume') {
      const rounded = Math.round(Number(value) * 10) / 10;
      return `${rounded} L`;
    }
    const inches = Math.round(Number(value));
    return `${Math.floor(inches / 12)}'${inches % 12}"`;
  }

  function displayValue(item, key) {
    // Preserve Shopify's exact filter label (including grouped ranges such as 7'0" - 7'5")
    // instead of reconstructing it from the numeric starting value.
    return item && item.label ? item.label : formatValue(item?.value, key);
  }

  function getFilterKey(details) {
    const dataKey = details.dataset.rsRangeKey;
    if (dataKey && RANGE_LABELS[dataKey]) return dataKey;
    const label = (details.querySelector('.facets__summary-label, .mobile-facets__summary span')?.textContent || '').trim().toLowerCase();
    if (label.includes('length')) return 'length';
    if (label.includes('volume')) return 'volume';
    return null;
  }

  function getValues(details, key) {
    const inputs = [...details.querySelectorAll('input[type="checkbox"]')];
    const values = inputs.map((input) => {
      const label = input.closest('label');
      const text = label?.querySelector('.facet-checkbox__text-label')?.textContent || label?.textContent || input.value;
      return { input, value: parseValue(text, key), label: String(text || '').trim().replace(/\s+/g, ' ') };
    }).filter((item) => Number.isFinite(item.value));

    const unique = [];
    const seen = new Set();
    values.sort((a, b) => a.value - b.value).forEach((item) => {
      if (!seen.has(item.value)) {
        seen.add(item.value);
        unique.push(item);
      }
    });
    return unique;
  }

  function getTarget(details) {
    const isDesktop = Boolean(details.closest('#FacetFiltersForm')) && !details.closest('#FacetFiltersFormMobile');
    if (isDesktop) return document.querySelector('[data-rs-top-range-filters]');
    return document.querySelector('[data-rs-mobile-range-filters]') || details.querySelector('.mobile-facets__submenu');
  }

  function getExisting(target, key) {
    if (!target) return null;
    return target.querySelector(`.rs-range-filter[data-rs-range-instance="${key}"]`);
  }

  function indexForValue(values, value, fallback) {
    const exact = values.findIndex((item) => item.value === value);
    return exact >= 0 ? exact : fallback;
  }

  function createRange(details, key, target, values) {
    if (!target || values.length < 2) return null;

    const active = values.filter((item) => item.input.checked).map((item) => item.value);
    const selectedMinValue = active.length ? Math.min(...active) : values[0].value;
    const selectedMaxValue = active.length ? Math.max(...active) : values[values.length - 1].value;
    let minIndex = indexForValue(values, selectedMinValue, 0);
    let maxIndex = indexForValue(values, selectedMaxValue, values.length - 1);

    const wrap = document.createElement('div');
    wrap.className = 'rs-range-filter';
    wrap.dataset.key = key;
    wrap.dataset.rsRangeInstance = key;
    wrap.innerHTML = `
      <div class="rs-range-filter__heading">${key === 'length' ? 'Surfboard Length' : 'Surfboard Volume'}</div>
      <div class="rs-range-filter__values"><span class="rs-range-filter__min"></span><span class="rs-range-filter__max"></span></div>
      <div class="rs-range-filter__control" role="group" aria-label="${key === 'length' ? 'Surfboard Length' : 'Surfboard Volume'} range">
        <div class="rs-range-filter__track" aria-hidden="true"><span></span></div>
        <button type="button" class="rs-range-filter__handle rs-range-filter__handle--min" aria-label="Minimum ${key}" role="slider" aria-valuemin="0" aria-valuemax="${values.length - 1}" aria-valuenow="${minIndex}"></button>
        <button type="button" class="rs-range-filter__handle rs-range-filter__handle--max" aria-label="Maximum ${key}" role="slider" aria-valuemin="0" aria-valuemax="${values.length - 1}" aria-valuenow="${maxIndex}"></button>
      </div>
    `;
    target.appendChild(wrap);

    const minHandle = wrap.querySelector('.rs-range-filter__handle--min');
    const maxHandle = wrap.querySelector('.rs-range-filter__handle--max');
    const minText = wrap.querySelector('.rs-range-filter__min');
    const maxText = wrap.querySelector('.rs-range-filter__max');
    const control = wrap.querySelector('.rs-range-filter__control');
    const fill = wrap.querySelector('.rs-range-filter__track span');

    let activeHandle = null;
    let activePointerId = null;
    let pendingApply = false;
    let rafPending = false;

    function clampIndexes() {
      minIndex = Math.max(0, Math.min(values.length - 1, minIndex));
      maxIndex = Math.max(0, Math.min(values.length - 1, maxIndex));
      if (minIndex > maxIndex) {
        if (activeHandle === 'min') minIndex = maxIndex;
        else maxIndex = minIndex;
      }
    }

    function updateVisuals() {
      clampIndexes();
      const denom = Math.max(1, values.length - 1);
      const minPct = (minIndex / denom) * 100;
      const maxPct = (maxIndex / denom) * 100;
      minText.textContent = displayValue(values[minIndex], key);
      maxText.textContent = displayValue(values[maxIndex], key);
      minHandle.style.left = `${minPct}%`;
      maxHandle.style.left = `${maxPct}%`;
      fill.style.left = `${minPct}%`;
      fill.style.right = `${100 - maxPct}%`;
      minHandle.setAttribute('aria-valuenow', String(minIndex));
      maxHandle.setAttribute('aria-valuenow', String(maxIndex));
      minHandle.setAttribute('aria-valuetext', displayValue(values[minIndex], key));
      maxHandle.setAttribute('aria-valuetext', displayValue(values[maxIndex], key));
      minHandle.style.zIndex = activeHandle === 'min' ? '4' : '3';
      maxHandle.style.zIndex = activeHandle === 'max' ? '4' : '3';
    }

    function renderSoon() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        updateVisuals();
      });
    }

    function clientXFromEvent(event) {
      if (event.touches && event.touches.length) return event.touches[0].clientX;
      if (event.changedTouches && event.changedTouches.length) return event.changedTouches[0].clientX;
      return event.clientX;
    }

    function indexFromClientX(clientX) {
      const rect = control.getBoundingClientRect();
      const usableLeft = rect.left;
      const usableWidth = rect.width;
      const x = Math.max(0, Math.min(usableWidth, clientX - usableLeft));
      return Math.round((x / usableWidth) * (values.length - 1));
    }

    function setIndexFromClientX(clientX) {
      const index = indexFromClientX(clientX);
      if (activeHandle === 'min') minIndex = Math.min(index, maxIndex);
      if (activeHandle === 'max') maxIndex = Math.max(index, minIndex);
      pendingApply = true;
      renderSoon();
    }

    function beginDrag(handle, event) {
      event.preventDefault();
      activeHandle = handle;
      wrap.dataset.dragging = 'true';
      activePointerId = event.pointerId ?? null;
      if (event.pointerId != null && event.currentTarget.setPointerCapture) {
        try { event.currentTarget.setPointerCapture(event.pointerId); } catch (_) {}
      }
      setIndexFromClientX(clientXFromEvent(event));
    }

    function movePointer(event) {
      if (!activeHandle) return;
      if (activePointerId != null && event.pointerId != null && event.pointerId !== activePointerId) return;
      event.preventDefault();
      setIndexFromClientX(clientXFromEvent(event));
    }

    function endDrag() {
      if (!activeHandle) return;
      const shouldApply = pendingApply;
      activeHandle = null;
      activePointerId = null;
      pendingApply = false;
      updateVisuals();
      if (shouldApply) {
        // Keep this instance protected from the MutationObserver while Shopify
        // is replacing the filter markup after the final selection is applied.
        applyFilter();
      } else {
        wrap.dataset.dragging = 'false';
      }
    }

    function applyFilter() {
      const lo = values[minIndex].value;
      const hi = values[maxIndex].value;
      getValues(details, key).forEach(({ input, value }) => {
        const shouldBeChecked = value >= lo && value <= hi;
        if (input.checked !== shouldBeChecked) {
          input.checked = shouldBeChecked;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
      updateVisuals();
      window.setTimeout(() => {
        if (wrap.isConnected && !activeHandle) wrap.dataset.dragging = 'false';
      }, 1600);
    }

    function keyboardAdjust(which, delta) {
      activeHandle = which;
      wrap.dataset.dragging = 'true';
      if (which === 'min') minIndex = Math.max(0, Math.min(minIndex + delta, maxIndex));
      else maxIndex = Math.min(values.length - 1, Math.max(maxIndex + delta, minIndex));
      updateVisuals();
      applyFilter();
      activeHandle = null;
      wrap.dataset.dragging = 'false';
      updateVisuals();
    }

    minHandle.addEventListener('pointerdown', (event) => beginDrag('min', event));
    maxHandle.addEventListener('pointerdown', (event) => beginDrag('max', event));
    minHandle.addEventListener('pointermove', movePointer, { passive: false });
    maxHandle.addEventListener('pointermove', movePointer, { passive: false });
    minHandle.addEventListener('pointerup', endDrag);
    maxHandle.addEventListener('pointerup', endDrag);
    minHandle.addEventListener('pointercancel', endDrag);
    maxHandle.addEventListener('pointercancel', endDrag);

    // Touch fallback for browsers where pointer events are unreliable on button controls.
    function beginTouch(handle, event) {
      event.preventDefault();
      activeHandle = handle;
      wrap.dataset.dragging = 'true';
      activePointerId = null;
      setIndexFromClientX(clientXFromEvent(event));
    }
    function moveTouch(event) {
      if (!activeHandle) return;
      event.preventDefault();
      setIndexFromClientX(clientXFromEvent(event));
    }
    minHandle.addEventListener('touchstart', (event) => beginTouch('min', event), { passive: false });
    maxHandle.addEventListener('touchstart', (event) => beginTouch('max', event), { passive: false });
    document.addEventListener('touchmove', moveTouch, { passive: false });
    document.addEventListener('touchend', endDrag, { passive: true });
    document.addEventListener('touchcancel', endDrag, { passive: true });

    // Mouse fallback.
    function beginMouse(handle, event) {
      if (event.button !== 0) return;
      event.preventDefault();
      activeHandle = handle;
      wrap.dataset.dragging = 'true';
      activePointerId = null;
      setIndexFromClientX(event.clientX);
    }
    function moveMouse(event) {
      if (!activeHandle) return;
      event.preventDefault();
      setIndexFromClientX(event.clientX);
    }
    minHandle.addEventListener('mousedown', (event) => beginMouse('min', event));
    maxHandle.addEventListener('mousedown', (event) => beginMouse('max', event));
    document.addEventListener('mousemove', moveMouse, { passive: false });
    document.addEventListener('mouseup', endDrag);

    [minHandle, maxHandle].forEach((handle, i) => {
      handle.addEventListener('keydown', (event) => {
        const which = i === 0 ? 'min' : 'max';
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') { event.preventDefault(); keyboardAdjust(which, -1); }
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') { event.preventDefault(); keyboardAdjust(which, 1); }
        if (event.key === 'Home') { event.preventDefault(); keyboardAdjust(which, which === 'min' ? -minIndex : minIndex - maxIndex); }
        if (event.key === 'End') { event.preventDefault(); keyboardAdjust(which, which === 'min' ? maxIndex - minIndex : values.length - 1 - maxIndex); }
      });
    });

    control.addEventListener('pointerdown', (event) => {
      if (event.target.closest('.rs-range-filter__handle')) return;
      const index = indexFromClientX(clientXFromEvent(event));
      activeHandle = Math.abs(index - minIndex) <= Math.abs(index - maxIndex) ? 'min' : 'max';
      setIndexFromClientX(clientXFromEvent(event));
      endDrag();
    });

    updateVisuals();
    return wrap;
  }

  function syncExisting(details, key, wrap, values) {
    if (!wrap || values.length < 2 || wrap.dataset.dragging === 'true') return;
    const active = values.filter((item) => item.input.checked).map((item) => item.value);
    const selectedMinValue = active.length ? Math.min(...active) : values[0].value;
    const selectedMaxValue = active.length ? Math.max(...active) : values[values.length - 1].value;
    const minIndex = indexForValue(values, selectedMinValue, 0);
    const maxIndex = indexForValue(values, selectedMaxValue, values.length - 1);
    const minHandle = wrap.querySelector('.rs-range-filter__handle--min');
    const maxHandle = wrap.querySelector('.rs-range-filter__handle--max');
    const minText = wrap.querySelector('.rs-range-filter__min');
    const maxText = wrap.querySelector('.rs-range-filter__max');
    const fill = wrap.querySelector('.rs-range-filter__track span');
    const denom = Math.max(1, values.length - 1);
    minText.textContent = displayValue(values[minIndex], key);
    maxText.textContent = displayValue(values[maxIndex], key);
    minHandle.style.left = `${(minIndex / denom) * 100}%`;
    maxHandle.style.left = `${(maxIndex / denom) * 100}%`;
    fill.style.left = `${(minIndex / denom) * 100}%`;
    fill.style.right = `${100 - ((maxIndex / denom) * 100)}%`;
  }

  function setupRange(details) {
    const key = getFilterKey(details);
    if (!key) return;
    const isDesktop = Boolean(details.closest('#FacetFiltersForm')) && !details.closest('#FacetFiltersFormMobile');
    const target = getTarget(details);
    if (!target) return;
    const values = getValues(details, key);
    if (values.length < 2) return;

    let existing = getExisting(target, key);
    if (!existing) existing = createRange(details, key, target, values);
    else syncExisting(details, key, existing, values);

    if (isDesktop && document.querySelector('[data-rs-top-range-filters]')) details.classList.add('rs-range-source');
    else if (!isDesktop && document.querySelector('[data-rs-mobile-range-filters]')) details.classList.add('rs-range-source-mobile');
  }

  function init() {
    document.querySelectorAll('.js-filter[data-rs-range-key], #FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter').forEach(setupRange);
  }

  function start() {
    init();
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        init();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
